
import { QStepper } from 'quasar'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import PaymentMode from '@/shared/enums/PaymentMode'
import Step from '@/shared/enums/Step'
import SubscriptionScheme from '@/shared/enums/SubscriptionScheme'

import CheckoutForm from '../models/CheckoutForm'
import StepItem from '../models/StepItem'

import AddressForm from './AddressForm.vue'
import BankTransferForm from './BankTransferForm.vue'
import CreditCardForm from './CreditCardForm.vue'
import PersonalInfoForm from './PersonalInfoForm.vue'
import SubscriptionForm from './SubscriptionForm.vue'
import CheckoutSummary from './Summary.vue'

import EButton from './Button.vue'

import { Action, State } from 'vuex-class'

@Component({
  components: {
    CreditCardForm,
    PersonalInfoForm,
    AddressForm,
    EButton,
    BankTransferForm,
    SubscriptionForm,
    CheckoutSummary,
  },
  computed: {
    Step: () => Step,
  },
})
export default class Form extends Vue {
  step = Step.infoForm

  autoSearchAddress = true

  $refs!: {
    stepper: QStepper
    infoForm: PersonalInfoForm[]
    addressForm: AddressForm[]
    cardForm: CreditCardForm[]
    bankTransferForm: BankTransferForm[]
    subscriptionForm: SubscriptionForm[]
  }

  @Prop() readonly data!: CheckoutForm

  @Prop({ required: true }) readonly amount!: number

  @Prop({ required: false, default: 'BRL' }) readonly currency!: string

  @Prop({ type: Boolean, default: false }) readonly use3ds!: boolean

  @Action('setDisableAddress') setDisableAddress!: (value: boolean) => void

  @State('disableAddress') disableAddress!: boolean

  steps = new Map<Step, StepItem>();

  mounted() {
    const { country } = this.data.payment
    this.autoSearchAddress = country === 'BR'

    this.setDisableAddress(!!this.data.disableAddress)

    this.makeSteps()
  }

  get isHeaderHidden(): boolean {
    return this.steps.size === 1 || this.step === Step.subscriptionForm
  }

  get isSubscription(): boolean {
    return Boolean(this.data.subscription)
  }

  get isPixSubscription(): boolean {
    return this.isSubscriptionOf(SubscriptionScheme.pixAutomatico)
  }

  get isCardSubscription(): boolean {
    return this.isSubscriptionOf(SubscriptionScheme.card)
  }

  get buttonLabel(): string {
    if (this.isPixSubscription) {
      return this.$tc('message.generateQrCode')
    }

    if (this.data.payment.method === PaymentMode.qrInteroperable || this.data.payment.method === PaymentMode.breb) {
      return this.$tc('message.qrInteroperable.generateQr')
    }

    if (this.step === Step.cardForm) {
      return this.$tc('message.payNow')
    }

    if (this.step === Step.subscriptionForm) {
      return this.$tc('message.nextStep')
    }

    return this.$tc('message.confirm')
  }

  @Watch('data.disableAddress')
  onDisableAddressChange(newVal: boolean) {
    this.setDisableAddress(newVal)
  }

  private makeSteps() {
    const items = new Map<Step, StepItem>()
    const { method, country } = this.data.payment

    const isNotBrazil = country !== 'BR'

    if (this.isSubscription) {
      items.set(Step.subscriptionForm, {
        step: Step.subscriptionForm,
        title: 'Subscription',
        icon: 'subscriptions',
        activeIcon: 'subscriptions',
        doneIcon: 'subscriptions',
        ref: 'subscriptionForm',
        component: 'subscription-form',
        next: () => {
          const { stepper, subscriptionForm } = this.$refs

          if (this.isCardSubscription) {
            stepper.next()
            items.delete(Step.subscriptionForm)
            return true
          }

          if (subscriptionForm[0].validateFields()) {
            this.submit()
            return true
          }

          return false
        },
      })

      this.steps = items
      this.step = Step.subscriptionForm

      if (this.isPixSubscription) {
        return
      }
    }

    items.set(Step.infoForm, {
      step: Step.infoForm,
      title: 'Personal Info',
      icon: 'person',
      activeIcon: 'person',
      doneIcon: 'person',
      ref: 'infoForm',
      component: 'personal-info-form',
      next: () => {
        const { stepper, infoForm } = this.$refs

        if (infoForm[0].validateFields()) {
          if (items.size <= 1) {
            this.submit()
          } else {
            stepper.next()
          }

          return true
        }

        return false
      },
    })

    if (!this.data.disableAddress && !isNotBrazil) {
      items.set(Step.addressForm, {
        step: Step.addressForm,
        title: 'Address',
        icon: 'location_on',
        activeIcon: 'location_on',
        doneIcon: 'location_on',
        ref: 'addressForm',
        component: 'address-form',
        next: () => {
          const { stepper, addressForm } = this.$refs

          if (addressForm[0].validateFields()) {
            if (items.size <= 2) {
              this.submit()
            } else {
              stepper.next()
            }

            return true
          }

          return false
        },
      })
    }

    if (method === PaymentMode.creditCard || this.isCardSubscription) {
      items.set(Step.cardForm, {
        step: Step.cardForm,
        title: 'Credit Card',
        icon: 'credit_card',
        activeIcon: 'credit_card',
        doneIcon: 'credit_card',
        ref: 'cardForm',
        component: 'credit-card-form',
        next: () => {
          const { cardForm } = this.$refs

          if (cardForm[0].validateFields()) {
            this.submit()
            return true
          }

          return false
        },
      })
    } else if (method === PaymentMode.debitCard) {
      items.set(Step.cardForm, {
        step: Step.cardForm,
        title: 'Debit Card',
        icon: 'credit_card',
        activeIcon: 'credit_card',
        doneIcon: 'credit_card',
        ref: 'cardForm',
        component: 'credit-card-form',
        next: () => {
          const { cardForm } = this.$refs

          if (cardForm[0].validateFields()) {
            this.submit()
            return true
          }

          return false
        },
      })
    } else if (method === PaymentMode.bankTransfer && (country === 'CO' || country === 'AR')) {
      items.set(Step.bankTransferForm, {
        step: Step.bankTransferForm,
        title: 'Bank Transfer',
        icon: 'account_balance',
        activeIcon: 'account_balance',
        doneIcon: 'account_balance',
        ref: 'bankTransferForm',
        component: 'bank-transfer-form',
        next: () => {
          const { bankTransferForm } = this.$refs

          if (bankTransferForm[0].validateFields()) {
            this.submit()
            return true
          }

          return false
        },
      })
    }

    this.steps = items
  }

  onSubmit(): void {
    const stepItem = this.steps.get(this.step)

    if (stepItem?.next()) {
      this.$emit('step', this.step + 1)
    }
  }

  submit(): void {
    const evt = this.isPixSubscription || !this.use3ds ? 'submit' : 'authenticate'

    this.$emit(evt, this.data)
  }

  goToStep(step: number) {
    this.step = step
  }

  private isSubscriptionOf(scheme: SubscriptionScheme): boolean {
    const { subscription } = this.data

    if (!subscription) {
      return false;
    }

    return subscription.scheme === scheme;
  }
}
